/**
 * Portal selection for the Bedrock Model Configuration console (Story #5164
 * Stream 2). Modelled directly on EnvironmentContext.tsx's pattern (URL
 * ?portal= > localStorage > default), so the choice persists across all 5
 * screens (Model Configuration, picker drawer, Catalogue, Usage & Cost, Cost
 * Projection) without prop drilling.
 *
 * PORTAL ID SOURCE OF TRUTH (finding, session 20260826-BEDROCK17-L): this
 * list uses the Bedrock call-site registry's real `owning_portal` values
 * (lambda/layers/secureembed-shared/python/model_config_site_roles.py,
 * vendored into lambda/dashboard/model-config/ -- see that Lambda's
 * handler.py docstring), NOT dashboard/src/pages/Portals.tsx's `poolKey`
 * field. The two disagree for two of the eight customer portals: the
 * registry uses 'tertiary-academic' where Portals.tsx's poolKey says
 * 'academic', and 'innovateflow-api' where poolKey says 'innovateflow'.
 * Since GET /api/model-config?portal=<id> matches against
 * `portal#{owning_portal}` rows written by the registry-driven backend, this
 * selector must use the registry's own strings -- using poolKey here would
 * silently query a scope that no row is ever written under, and every
 * portal's role table would render empty with no error. The registry also
 * carries 4 non-customer buckets (patents-collaborators, shared-layer-copies,
 * shared-search-standalone, unmapped) for internal code classification --
 * those are deliberately NOT customer portals and are not offered here.
 */
import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

export interface ModelConfigPortalOption {
  id: string; // registry owning_portal value, or '_platform' for the platform default
  label: string;
}

export const MODEL_CONFIG_PORTALS: ModelConfigPortalOption[] = [
  { id: '_platform', label: 'Platform Default' },
  { id: 'food', label: 'Food' },
  { id: 'energy', label: 'Energy' },
  { id: 'education', label: 'Education' },
  { id: 'medicine', label: 'Medicine' },
  { id: 'tertiary-academic', label: 'Tertiary' },
  { id: 'innovateflow-api', label: 'InnovateFlow' },
  { id: 'insurance', label: 'Insurance' },
  { id: 'theology', label: 'Theology' },
];

const STORAGE_KEY = 'dashboard.modelConfig.portal';
const DEFAULT_PORTAL_ID = '_platform';

function isValidPortalId(v: unknown): v is string {
  return typeof v === 'string' && MODEL_CONFIG_PORTALS.some((p) => p.id === v);
}

// The two id schemes documented above (registry owning_portal vs P01's
// poolKey) disagree for two portals. Anything linking in from the P01
// catalogue (dashboard/src/pages/Portals.tsx) must translate through this
// map rather than assume the ids match -- an unmapped id (management,
// stakeholder: not in the Bedrock call-site registry, no role table exists
// for them) returns null so the caller can omit the link entirely instead
// of linking to a portal scope that silently renders empty.
const CATALOGUE_ID_OVERRIDES: Record<string, string> = {
  academic: 'tertiary-academic',
  innovateflow: 'innovateflow-api',
};

export function catalogueIdToModelConfigPortalId(catalogueId: string): string | null {
  const resolved = CATALOGUE_ID_OVERRIDES[catalogueId] ?? catalogueId;
  return MODEL_CONFIG_PORTALS.some((p) => p.id === resolved) ? resolved : null;
}

function readInitialPortal(): string {
  try {
    const fromUrl = new URLSearchParams(window.location.search).get('portal');
    if (isValidPortalId(fromUrl)) return fromUrl;
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isValidPortalId(stored)) return stored;
  } catch {
    /* storage / URL unavailable -- fall through to default */
  }
  return DEFAULT_PORTAL_ID;
}

interface ModelConfigPortalContextValue {
  portalId: string;
  setPortalId: (id: string) => void;
  portals: ModelConfigPortalOption[];
}

const ModelConfigPortalContext = createContext<ModelConfigPortalContextValue | undefined>(undefined);

export const ModelConfigPortalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const [portalId, setPortalIdState] = useState<string>(readInitialPortal);

  // queryClient.removeQueries() has side effects (it synchronously notifies every
  // subscribed useQuery observer, which calls setState on those components). Calling
  // it from inside the setPortalIdState updater ran it during React's render phase --
  // exactly the "update a component while rendering a different component" hazard
  // React warns about -- which is why the AI & model defaults screen could get stuck
  // on its loading spinner after a portal switch (found live, Story #6280 Stream 7
  // follow-up). Comparing against `portalId` directly (already in scope) lets the
  // side effects move to plain code in the callback, after the state update.
  const setPortalId = useCallback((id: string) => {
    if (portalId === id) return;
    try { window.localStorage.setItem(STORAGE_KEY, id); } catch { /* ignore */ }
    try {
      const url = new URL(window.location.href);
      url.searchParams.set('portal', id);
      window.history.replaceState({}, '', url.toString());
    } catch { /* ignore */ }
    setPortalIdState(id);
    // Datastores are platform-level (do not vary by portalId, see ConfigurationAiModels.tsx) --
    // only drop the per-portal resolved-config cache, not the datastores cache too.
    queryClient.removeQueries({ queryKey: ['model-config', 'resolved'] });
  }, [portalId, queryClient]);

  useEffect(() => {
    try {
      if (!window.localStorage.getItem(STORAGE_KEY)) {
        window.localStorage.setItem(STORAGE_KEY, portalId);
      }
    } catch { /* ignore */ }
  }, [portalId]);

  return (
    <ModelConfigPortalContext.Provider value={{ portalId, setPortalId, portals: MODEL_CONFIG_PORTALS }}>
      {children}
    </ModelConfigPortalContext.Provider>
  );
};

export function useModelConfigPortal(): ModelConfigPortalContextValue {
  const ctx = useContext(ModelConfigPortalContext);
  if (!ctx) throw new Error('useModelConfigPortal must be used within a ModelConfigPortalProvider');
  return ctx;
}
